import { useState } from 'react'

import { autoAssignStatuses } from '~/app/pages/SurgeonScheduler/TablesLayout/shared'
import { day, type DndHover } from '~/astra'
import { Schemas } from '~/clients/api-client'
import {
    partialUpdatePractitionerScheduleLocation,
    partialUpdatePractitionerScheduleStatus,
    updateBatchPractitionerScheduleLocation,
} from '~/store/di-entity.api.generated'
import { useStore } from '~/store/store'
import { Practitioner, selectPractitionerScheduleLocations, usePractitionerScheduleLocations, usePractitionerScheduleStatuses } from '~/storm'

import { type PractitionerActivity, type PractitionerStatusActivity } from '../../../PractitionerActivityDnd'

type Props = {
    practitionerId: Practitioner['id']
    date: string
}

export const useActivityDrop = ({ practitionerId, date }: Props) => {
    const [preview, setPreview] = useState<PractitionerActivity | null>(null)
    const statusByDefinitionUuid = usePractitionerScheduleStatuses({
        where: { practitioner_id: practitionerId, scheduled_date: date },
        include: { definition: true },
        keyBy: 'practitioner_status_definition_uuid',
    })
    const scheduleLocationsByLocationId = usePractitionerScheduleLocations({
        where: { practitioner_id: practitionerId, scheduled_date: date },
        include: { location: true },
        keyBy: 'location_id',
    })

    const canDrop = ({ payload }: { payload: PractitionerActivity }) => {
        if (payload.scheduled_date === date && payload.practitioner_id === practitionerId) return false

        if (payload.entity_type === 'practitioner_schedule_location') {
            return !scheduleLocationsByLocationId.get(payload.location_id)
        }
        // if the status carries locations that are not already assigned, allow dropping
        if (payload.locations.length > 0) {
            return payload.locations.some(location => !scheduleLocationsByLocationId.has(location.id))
        }
        // else disallow dropping duplicate statuses
        return !statusByDefinitionUuid.has(payload.practitioner_status_definition_uuid)
    }

    const handleDropScheduleStatus = async (activity: PractitionerStatusActivity) => {
        const state = useStore.getState()

        const updatePromises: Promise<unknown>[] = []
        if (!statusByDefinitionUuid.has(activity.practitioner_status_definition_uuid)) {
            updatePromises.push(
                partialUpdatePractitionerScheduleStatus(activity.id, {
                    practitioner_id: practitionerId,
                    scheduled_date: date,
                    status_code: activity.status_code,
                    practitioner_status_definition_uuid: activity.practitioner_status_definition_uuid,
                })
            )
        }
        if (activity.locations.length > 0) {
            const scheduleLocationsToMove = selectPractitionerScheduleLocations(state, {
                where: {
                    practitioner_uuid: activity.practitioner_uuid,
                    scheduled_date: activity.scheduled_date,
                    location_id: {
                        in: activity.locations.map(location => location.id).filter(id => !scheduleLocationsByLocationId.has(id)),
                    },
                },
            })
            const batchData: Schemas['BaseEntitySchemaUpdate_PractitionerScheduleLocationCreate_'][] = scheduleLocationsToMove.map(scheduleLocation => ({
                id: scheduleLocation.id,
                uuid: scheduleLocation.uuid,
                body: {
                    location_id: scheduleLocation.location_id,
                    practitioner_id: practitionerId,
                    scheduled_date: date,
                },
            }))
            updatePromises.push(updateBatchPractitionerScheduleLocation(batchData))
        }

        return Promise.all(updatePromises)
    }

    const onDrop = async ({ payload }: { payload: PractitionerActivity }) => {
        if (payload.entity_type === 'practitioner_schedule_location') {
            await Promise.all([
                partialUpdatePractitionerScheduleLocation(payload.id, {
                    practitioner_id: practitionerId,
                    scheduled_date: date,
                    location_id: payload.location_id,
                }),
                autoAssignStatuses([{ date: day(date), locationId: payload.location_id, practitionerId }]),
            ])
            return
        }

        await handleDropScheduleStatus(payload)
    }

    const onHoverChange = (hover: DndHover<PractitionerActivity> | null) => {
        setPreview(hover?.canDrop === true ? hover.payload : null)
    }

    const toBeDroppedLocation = preview?.entity_type === 'practitioner_schedule_location' ? preview : null
    const toBeDroppedStatus = preview?.entity_type === 'practitioner_schedule_status' ? preview : null

    return { dropProps: { canDrop, onDrop, onHoverChange }, toBeDroppedLocation, toBeDroppedStatus } as const
}
